import "@/config";
import "@/bootstrap/network";
import "./boot/fetch-shim";

import React from "react";
import { createRoot } from "react-dom/client";
import { prepareDesignEditor } from "@canva/intents/design";

// Import the main app component and full UI builder
import PhotoBookApp from "./app-root";
import { buildSidebar } from "./main";
import { initAutoCreateDesign } from "./utils/auto-create-design";

// Create a React wrapper for the full UI
const FullPhotoBookEditor: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (containerRef.current) {
      // Diagnostics logging
      const inCanva = !!(window as any).__CANVA_HOST__;
      const inDev = !inCanva && (window.location.host.includes('localhost') || window.location.host.includes('127.0.0.1'));
      const authAvailable = typeof auth !== 'undefined' && !!auth.getCanvaUserToken;

      console.log("[shell] mounted", {
        inCanva,
        inDev,
        authAvailable,
        host: window.location.host,
        activeTab: localStorage.getItem('photobook:activeTab') || 'templates'
      });

      console.log("[panel:templates] registered");
      console.log("[panel:layouts] registered");
      console.log("[panel:photos] registered");
      console.log("[panel:export] registered");

      // Mount the full UI with tabs (Templates, Layouts, Photos, Export)
      buildSidebar(containerRef.current);

      console.log("[shell] full UI mounted with all panels");

      // Auto-create photobook design on first load
      initAutoCreateDesign();
    }
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    />
  );
};

console.log("[entry] Canva app entry point loaded");
console.info("[build] ts:", Date.now());

// Prepare the edit_design intent with the required render action
prepareDesignEditor({
  render: () => {
    console.log("[intents] edit_design:render action triggered");

    // Set Canva host flag to prevent dev mode detection
    (window as any).__CANVA_HOST__ = true;

    // Mount exactly once
    let container = document.getElementById("root");
    if (!container) {
      container = document.createElement("div");
      container.id = "root";
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.height = "100%";
      document.body.appendChild(container);
    }

    // Create or reuse React root
    const root = (container as any).__root ?? createRoot(container);
    (container as any).__root = root;

    // Render the full app with tabs
    root.render(<FullPhotoBookEditor />);
  }
});

// Optional: Support for local development outside Canva
if (typeof window !== 'undefined' && !(window as any).__CANVA_HOST__) {
  console.log("[entry] Local development mode detected");
  
  // Simulate render for local debugging
  const container = document.getElementById("root") ?? (() => {
    const el = document.createElement("div");
    el.id = "root";
    el.style.display = "flex";
    el.style.flexDirection = "column";
    el.style.height = "100%";
    document.body.appendChild(el);
    return el;
  })();
  
  const root = (container as any).__root ?? createRoot(container);
  (container as any).__root = root;
  root.render(<FullPhotoBookEditor />);
}
